import { useMemo } from 'react';
import { MdOutlineClear, MdViewList, MdPerson, MdApps } from 'react-icons/md';
import { Button } from '@ds';
import { IconButtonGroup } from '@ui';
import { WorkdaySyncData } from '@lp/features/Scheduler/types';
import { FilterOptionsDropdown, SchedulerResourcesFilter } from '@lp/features/Scheduler/components';
import { scheduleFiltersSelectors, useOMSSchedulerFiltersStore } from '@lp/core/store/scheduler';
import { OMSSchedulerViewType } from '@lp/features/Scheduler/SchedulerOMS';
import { OMSResourcesWithShifts } from '@/api/apis/resources/schedule';

type OMSSchedulerFiltersSectionProps = {
  readonly?: boolean;
  shiftsData: OMSResourcesWithShifts;
  workdayData?: WorkdaySyncData;
  viewMode: OMSSchedulerViewType;
  withPersonalView?: boolean;
  onViewModeChange: (mode: OMSSchedulerViewType) => void;
};

export const OMSSchedulerFiltersSection = ({
  readonly,
  shiftsData,
  workdayData,
  viewMode,
  withPersonalView,
  onViewModeChange,
}: OMSSchedulerFiltersSectionProps) => {
  const hasActiveFilters = useOMSSchedulerFiltersStore(scheduleFiltersSelectors.hasActiveFilters);
  const filters = useOMSSchedulerFiltersStore();
  const isScheduleView = viewMode === OMSSchedulerViewType.Scheduler;
  const showResourcesFilter = isScheduleView || viewMode === OMSSchedulerViewType.Duty;

  const viewModeItems = useMemo(() => {
    const list = [
      { value: OMSSchedulerViewType.Scheduler, icon: <MdViewList /> },
      { value: OMSSchedulerViewType.Duty, icon: <MdApps /> },
    ];
    if (withPersonalView) list.push({ value: OMSSchedulerViewType.Personal, icon: <MdPerson /> });
    return list;
  }, [withPersonalView]);

  return (
    <div className="flex items-center gap-4">
      {hasActiveFilters && showResourcesFilter && (
        <Button disableRipple size="sm" variant="light" endContent={<MdOutlineClear />} onPress={filters.clearFilters}>
          Clear all
        </Button>
      )}
      {showResourcesFilter && (
        <SchedulerResourcesFilter
          shiftsData={shiftsData}
          selectedKeys={filters.resources}
          onSelectedKeysChange={(ids) => filters.setFilterByKey('resources', ids)}
        />
      )}
      {isScheduleView && (
        <FilterOptionsDropdown
          onlyStatusFilter={readonly}
          withShiftTotals
          shiftTotalsEnabled={filters.shiftTotals}
          onShiftTotalsChange={(enable) => filters.setFilterByKey('shiftTotals', enable)}
          statusFilterEnabled={filters.statusFilters}
          onStatusFilterChange={(enable) => filters.setFilterByKey('statusFilters', enable)}
          workdayData={workdayData}
        />
      )}
      <IconButtonGroup items={viewModeItems} value={viewMode} onValueChange={onViewModeChange} />
    </div>
  );
};
